import React, { useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useOffboardUser } from '~/hooks/apis/agent/useOffboardUser';
import { useOnboardUser } from '~/hooks/apis/agent/useOnboardUser';

import { Eoi, EoiStatus } from '~/types/services/agentHub';

interface Props {
  open: boolean;
  onClose: () => void;
  user?: Eoi;
  onUpdate?: () => void;
}

export default function AgencySubmissionToggleOnboardModal({ open, onClose, user, onUpdate }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const offboardMutation = useOffboardUser();
  const onboardMutation = useOnboardUser();

  const handleOffboard = async () => {
    if (!user) {
      enqueueSnackbar('User not found', { variant: 'error' });
      return;
    }
    offboardMutation.mutate(user.customerId, {
      onSuccess: () => {
        enqueueSnackbar('User offboarded successfully', { variant: 'success' });
        onClose();
        onUpdate?.();
      },
      onError: (error) => {
        enqueueSnackbar(`Error offboarding user: ${error.message}`, { variant: 'error' });
      },
    });
  };

  const handleOnboard = async () => {
    if (!user) {
      enqueueSnackbar('User not found', { variant: 'error' });
      return;
    }
    onboardMutation.mutate(user.customerId, {
      onSuccess: () => {
        enqueueSnackbar('User onboarded successfully', { variant: 'success' });
        onClose();
        onUpdate?.();
      },
      onError: (error) => {
        enqueueSnackbar(`Error onboarding user: ${error.message}`, { variant: 'error' });
      },
    });
  };

  const buttonText = useMemo(() => {
    if (user?.userStatus === EoiStatus.Onboarded) {
      return 'Offboard';
    }
    return 'Onboard';
  }, [user]);

  const text = useMemo(() => {
    if (user?.userStatus === EoiStatus.Onboarded) {
      return `Are you sure you want to offboard ${user?.givenName} ${user?.surname}?`;
    }
    return `Are you sure you want to onboard ${user?.givenName} ${user?.surname}?`;
  }, [user]);
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{buttonText}</DialogTitle>
      <DialogContent>
        <Typography>{text}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="secondary"
          onClick={onClose}
          disabled={offboardMutation.isPending || onboardMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={user?.userStatus === EoiStatus.Onboarded ? handleOffboard : handleOnboard}
          disabled={offboardMutation.isPending || onboardMutation.isPending}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
